import React from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { ShieldAlert } from "lucide-react";

/**
 * Global single-session kick-out popup.
 *
 * Rendered once at the app shell (inside <AuthProvider>) so any
 * component making an API call can trigger it via the axios
 * interceptor → `cocoon-session-invalidated` window event → the
 * `kickedOut` state on the auth context. Dismissing the modal
 * bounces the user to the correct login screen.
 */
export default function KickedOutDialog() {
  const { kickedOut, dismissKickedOut } = useAuth();
  const nav = useNavigate();
  const open = !!kickedOut;

  const onDismiss = () => {
    const kind = kickedOut?.kind;
    dismissKickedOut();
    if (kind === "customer") nav("/portal/login");
    else nav("/staff/login");
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onDismiss(); }}>
      <DialogContent className="max-w-md" data-testid="kicked-out-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-red-700">
            <ShieldAlert className="w-5 h-5" />
            Signed out
          </DialogTitle>
          <DialogDescription className="text-sm mt-2" data-testid="kicked-out-message">
            You have been logged out because this account is now logged in on another device.
          </DialogDescription>
        </DialogHeader>
        <DialogFooter className="mt-4">
          <Button onClick={onDismiss} data-testid="kicked-out-dismiss">Return to sign in</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
